import { z } from 'zod'
import type { CommandAnchor } from '../../lib/agent-command-anchor.js'
import { baseEntitySchema } from '../entity.js'
import { coreElementValueSchema } from '../slate/slate-element.js'

export const commentKinds = ['chat', 'nudge', 'proposal'] as const
export type CommentKind = (typeof commentKinds)[number]

export const commentSchema = baseEntitySchema.extend({
  type: z.literal('comment'),
  userId: z.string(),
  resolved: z.boolean().default(false).optional(),
  /**
   * What this comment IS: a chat turn, an agent nudge, or an agent proposal.
   * Optional (not defaulted) — legacy comments predate the field and are
   * recognized forever via the metadata-flag fallbacks in the guards below.
   * New writes stamp BOTH kind and the legacy flag during the deploy window.
   */
  kind: z.enum(commentKinds).describe('chat | nudge | proposal').optional(),
  children: coreElementValueSchema,
})

export type Comment = z.infer<typeof commentSchema>

export function isComment(node: unknown): node is Comment {
  return commentSchema.safeParse(node).success
}

const metadataFlag = (comment: Comment, flag: string): boolean =>
  typeof comment.metadata === 'object' &&
  comment.metadata !== null &&
  (comment.metadata as Record<string, unknown>)[flag] === true

export function isProposalComment(comment: Comment): boolean {
  return comment.kind === 'proposal' || metadataFlag(comment, 'agentProposal')
}

export function isNudgeComment(comment: Comment): boolean {
  return comment.kind === 'nudge' || metadataFlag(comment, 'agentNudge')
}

export function isChatComment(comment: Comment): boolean {
  return !isProposalComment(comment) && !isNudgeComment(comment)
}

export interface EditorCommandProposalInput {
  /** The agent the proposal is a child of */
  agentId: string
  /** The invoking user, granted READ; authorship is `assistant` */
  userId: string
  /** The entity the command edits, e.g. the song */
  targetEntityId: string
  summary: string
  commandId: string
  args?: Record<string, unknown>
  anchor: CommandAnchor
}

/**
 * The partial entity an editor-command proposal is persisted as: what the agent server writes when a
 * tool proposes a command (`propose_chord_change`, `run_editor_command`), and what E2E seeds, so a
 * seeded proposal is the same thing a real agent turn produces (GH #2352).
 *
 * Owned by `assistant` (the chat UI keys authorship off `userId === 'assistant'`) with READ for the
 * invoking user. `kind` and the legacy `metadata.agentProposal` flag are both stamped for the deploy
 * window. `anchorBlockId` lets the gutter chip find the proposal without reading the patch.
 */
export const editorCommandProposalEntity = (input: EditorCommandProposalInput) => ({
  type: 'comment' as const,
  parentId: input.agentId,
  userId: 'assistant',
  kind: 'proposal' as const,
  acls: [{ principal: input.userId, permission: 'READ' as const }],
  children: [{ type: 'line' as const, children: [{ text: `[proposal] ${input.summary}` }] }],
  metadata: {
    agentProposal: true,
    targetEntityId: input.targetEntityId,
    summary: input.summary,
    anchorBlockId: input.anchor.id,
    blockSpan: 1,
    patch: {
      kind: 'editor-command' as const,
      commandId: input.commandId,
      args: input.args,
      anchor: input.anchor,
    },
  },
})
